/**
 * =======================================================================
 * PERMISSION & AUTHORIZATION MODEL
 * =======================================================================
 * 
 * Security Best Practices:
 * - Granular permissions instead of monolithic roles
 * - Client-side permissions are for UI only (hide/show elements)
 * - ALWAYS validate permissions server-side before executing actions
 * - Never trust client-side checks alone
 * - Use enums for type safety and autocompletion
 * 
 * Penetration Test Considerations:
 * - All permission checks must be mirrored on backend
 * - Token tampering should be detected server-side
 * - Expired tokens should be rejected server-side
 * - Role escalation attempts should be logged
 * =======================================================================
 */

/**
 * Granular application permissions
 * Each permission represents a specific capability
 */
export enum Permission {
  // ===== SUPPLIERS =====
  VIEW_SUPPLIERS = 'view_suppliers',
  CREATE_SUPPLIER = 'create_supplier',
  EDIT_SUPPLIER = 'edit_supplier',
  DELETE_SUPPLIER = 'delete_supplier',
  APPROVE_SUPPLIER = 'approve_supplier',
  
  // ===== PROFESSIONALS (TRUSTEES) =====
  VIEW_PROFESSIONALS = 'view_professionals',
  CREATE_PROFESSIONAL = 'create_professional',
  EDIT_PROFESSIONAL = 'edit_professional',
  DELETE_PROFESSIONAL = 'delete_professional',
  APPROVE_PROFESSIONAL = 'approve_professional',
  VIEW_OWN_PROFILE = 'view_own_profile',
  EDIT_OWN_PROFILE = 'edit_own_profile',
  
  // ===== CONTRACTS =====
  VIEW_CONTRACTS = 'view_contracts',
  CREATE_CONTRACT = 'create_contract',
  EDIT_CONTRACT = 'edit_contract',
  DELETE_CONTRACT = 'delete_contract',
  SIGN_CONTRACT = 'sign_contract',
  APPROVE_CONTRACT = 'approve_contract',
  
  // ===== QUESTIONNAIRES =====
  VIEW_QUESTIONNAIRES = 'view_questionnaires',
  CREATE_QUESTIONNAIRE = 'create_questionnaire',
  EDIT_QUESTIONNAIRE = 'edit_questionnaire',
  DELETE_QUESTIONNAIRE = 'delete_questionnaire',
  SUBMIT_QUESTIONNAIRE = 'submit_questionnaire',
  
  // ===== TEMPLATES =====
  VIEW_TEMPLATES = 'view_templates',
  CREATE_TEMPLATE = 'create_template',
  EDIT_TEMPLATE = 'edit_template',
  DELETE_TEMPLATE = 'delete_template',
  
  // ===== CHECKLISTS =====
  VIEW_CHECKLISTS = 'view_checklists',
  CREATE_CHECKLIST = 'create_checklist',
  EDIT_CHECKLIST = 'edit_checklist',
  DELETE_CHECKLIST = 'delete_checklist',
  RUN_CHECKLIST = 'run_checklist',
  
  // ===== COLLABORATORS =====
  VIEW_COLLABORATORS = 'view_collaborators',
  ADD_COLLABORATOR = 'add_collaborator',
  EDIT_COLLABORATOR = 'edit_collaborator',
  REMOVE_COLLABORATOR = 'remove_collaborator',
  
  // ===== ADMIN =====
  VIEW_ADMIN_PANEL = 'view_admin_panel',
  MANAGE_USERS = 'manage_users',
  MANAGE_ROLES = 'manage_roles',
  VIEW_AUDIT_LOGS = 'view_audit_logs',
  SYSTEM_CONFIGURATION = 'system_configuration',
  
  // ===== REPORTING =====
  VIEW_REPORTS = 'view_reports',
  EXPORT_DATA = 'export_data',
  VIEW_ANALYTICS = 'view_analytics',
}

/**
 * Application roles
 * Each role has a predefined set of permissions
 */
export enum AppRole {
  SUPER_ADMIN = 'admin',
  BACKOFFICE_ADMIN = 'backoffice_admin',
  BACKOFFICE_PROFESSIONAL = 'backoffice_professional',
  BACKOFFICE_SUPPLIER = 'backoffice_supplier',
  PROFESSIONAL = 'professional',
  COLLABORATOR = 'collaborator',
}

/**
 * Role to Permissions mapping
 * CRITICAL: This must match server-side configuration
 */
export const ROLE_PERMISSIONS: Record<AppRole, Permission[]> = {
  [AppRole.SUPER_ADMIN]: [
    // Has all permissions
    ...Object.values(Permission),
  ],
  
  [AppRole.BACKOFFICE_ADMIN]: [
    // Suppliers
    Permission.VIEW_SUPPLIERS,
    Permission.CREATE_SUPPLIER,
    Permission.EDIT_SUPPLIER,
    Permission.DELETE_SUPPLIER,
    Permission.APPROVE_SUPPLIER,
    
    // Professionals
    Permission.VIEW_PROFESSIONALS,
    Permission.CREATE_PROFESSIONAL,
    Permission.EDIT_PROFESSIONAL,
    Permission.DELETE_PROFESSIONAL,
    Permission.APPROVE_PROFESSIONAL,
    
    // Contracts
    Permission.VIEW_CONTRACTS,
    Permission.CREATE_CONTRACT,
    Permission.EDIT_CONTRACT,
    Permission.DELETE_CONTRACT,
    Permission.APPROVE_CONTRACT,
    
    // Templates & Checklists
    Permission.VIEW_TEMPLATES,
    Permission.CREATE_TEMPLATE,
    Permission.EDIT_TEMPLATE,
    Permission.DELETE_TEMPLATE,
    Permission.VIEW_CHECKLISTS,
    Permission.CREATE_CHECKLIST,
    Permission.EDIT_CHECKLIST,
    Permission.DELETE_CHECKLIST,
    
    // Reporting
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
    Permission.VIEW_ANALYTICS,
  ],
  
  [AppRole.BACKOFFICE_PROFESSIONAL]: [
    // Professionals management only
    Permission.VIEW_PROFESSIONALS,
    Permission.CREATE_PROFESSIONAL,
    Permission.EDIT_PROFESSIONAL,
    Permission.APPROVE_PROFESSIONAL,
    
    // Questionnaires management
    Permission.VIEW_QUESTIONNAIRES,
    Permission.CREATE_QUESTIONNAIRE,
    Permission.EDIT_QUESTIONNAIRE,
    Permission.DELETE_QUESTIONNAIRE,
    
    // Contracts view only
    Permission.VIEW_CONTRACTS,
    
    // Reporting
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
  ],
  
  [AppRole.BACKOFFICE_SUPPLIER]: [
    // Suppliers management only
    Permission.VIEW_SUPPLIERS,
    Permission.CREATE_SUPPLIER,
    Permission.EDIT_SUPPLIER,
    Permission.APPROVE_SUPPLIER,
    
    // Contracts view only
    Permission.VIEW_CONTRACTS,
    
    // Reporting
    Permission.VIEW_REPORTS,
    Permission.EXPORT_DATA,
  ],
  
  [AppRole.PROFESSIONAL]: [
    // Own profile management
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    
    // Questionnaires
    Permission.VIEW_QUESTIONNAIRES,
    Permission.SUBMIT_QUESTIONNAIRE,
    
    // Contracts
    Permission.VIEW_CONTRACTS,
    Permission.SIGN_CONTRACT,
    
    // Collaborators
    Permission.VIEW_COLLABORATORS,
    Permission.ADD_COLLABORATOR,
    Permission.EDIT_COLLABORATOR,
    Permission.REMOVE_COLLABORATOR,
    
    // Checklists (run only)
    Permission.RUN_CHECKLIST,
  ],
  
  [AppRole.COLLABORATOR]: [
    // Limited access - only what assigned by professional
    Permission.VIEW_OWN_PROFILE,
    Permission.EDIT_OWN_PROFILE,
    Permission.VIEW_QUESTIONNAIRES,
    Permission.SUBMIT_QUESTIONNAIRE,
    Permission.VIEW_CONTRACTS,
  ],
};

/**
 * Complete user profile with roles and permissions
 * This is the single source of truth for authorization across the app
 */
export interface AppUser {
  // Identity (from JWT)
  id: string;
  email: string;
  name: string;
  
  // Authorization
  roles: AppRole[];
  permissions: Permission[];
  
  // Professional-specific (optional)
  professionalId?: string;
  trusteeType?: 'AUTO_EXPERT' | 'RE_EXPERT' | 'INVESTIGATOR' | 'DOCTOR' | 'LAWYER' | 'BODYSHOP';
  tipologiaCarrozzeria?: 'STANDARD' | 'ARS' | 'RCI' | 'AXA_DEUTSCHE_BANK';
  
  // Token metadata
  exp?: number; // Token expiration
  iat?: number; // Issued at
}

/**
 * Helper to create AppUser from JWT payload
 */
export function createAppUserFromToken(payload: any): AppUser {
  const roles = (payload.roles || []) as AppRole[];
  
  // Derive permissions from roles
  const permissions = Array.from(new Set(
    roles.flatMap(role => ROLE_PERMISSIONS[role] || [])
  ));
  
  return {
    id: payload.sub || payload.oid || payload.id || '',
    email: payload.email || payload.preferred_username || '',
    name: payload.name || payload.given_name || '',
    roles,
    permissions,
    professionalId: payload.professionalId,
    trusteeType: payload.trusteeType,
    tipologiaCarrozzeria: payload.tipologiaCarrozzeria,
    exp: payload.exp,
    iat: payload.iat,
  };
}

/**
 * Type guard to check if user has specific role
 */
export function hasRole(user: AppUser | null, role: AppRole): boolean {
  return user?.roles?.includes(role) ?? false;
}

/**
 * Type guard to check if user has specific permission
 */
export function hasPermission(user: AppUser | null, permission: Permission): boolean {
  return user?.permissions?.includes(permission) ?? false;
}

/**
 * Check if user has ANY of the specified permissions
 */
export function hasAnyPermission(user: AppUser | null, permissions: Permission[]): boolean {
  return permissions.some(p => hasPermission(user, p));
}

/**
 * Check if user has ALL of the specified permissions
 */
export function hasAllPermissions(user: AppUser | null, permissions: Permission[]): boolean {
  return permissions.every(p => hasPermission(user, p));
}

/**
 * Get human-readable role label
 */
export function getRoleLabel(role: AppRole): string {
  const labels: Record<AppRole, string> = {
    [AppRole.SUPER_ADMIN]: 'Super Amministratore',
    [AppRole.BACKOFFICE_ADMIN]: 'Amministratore Backoffice',
    [AppRole.BACKOFFICE_PROFESSIONAL]: 'Gestione Fiduciari',
    [AppRole.BACKOFFICE_SUPPLIER]: 'Gestione Fornitori',
    [AppRole.PROFESSIONAL]: 'Fiduciario',
    [AppRole.COLLABORATOR]: 'Collaboratore',
  };
  return labels[role] || role;
}
